import React, { useState, useEffect } from 'react';
import {
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Users,
  Database,
  Calendar,
  XCircle
} from 'lucide-react';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import facebookPagesService from '../../../services/integrations/facebookPagesService';

interface FacebookLeadForm {
  id: number;
  uuid: string;
  form_id: string;
  form_name: string;
  form_status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  page_id: string;
  created_time?: string;
  field_mapping?: any;
  last_sync_at?: string;
  total_leads_synced: number;
  is_active: boolean;
  formFields?: any[];
}

interface SyncResult {
  total?: number;
  synced: number;
  duplicates: number;
  errors: number;
  details?: any[];
}

interface FacebookLeadSyncProps {
  form: FacebookLeadForm;
  isOpen: boolean;
  onClose: () => void;
  onSync: (formId: string) => Promise<void>;
}

const FacebookLeadSync: React.FC<FacebookLeadSyncProps> = ({
  form,
  isOpen,
  onClose,
  onSync
}) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Date range state - default to last 30 days
  const getDefaultFromDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  };
  const [fromDate, setFromDate] = useState<string>(getDefaultFromDate());
  const [toDate, setToDate] = useState<string>(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setSyncResult(null);
      setSyncError(null);
      setFromDate(getDefaultFromDate());
      setToDate(new Date().toISOString().split('T')[0]);
    }
  }, [isOpen]);

  const handleSync = async () => {
    // Validate date range
    if (new Date(fromDate) > new Date(toDate)) {
      setSyncError('From date cannot be after To date');
      return;
    }

    setIsSyncing(true);
    setSyncError(null);
    setSyncResult(null);

    try {
      const response = await facebookPagesService.syncLeads(
        form.form_id,
        fromDate,
        toDate
      );

      if (response.success && response.data) {
        setSyncResult(response.data);
        // Refresh form data in parent
        await onSync(form.form_id);
      } else {
        setSyncError(response.message || 'Failed to sync leads');
      }
    } catch (error: any) {
      console.error('Error syncing leads:', error);
      setSyncError(error.message || 'Failed to sync leads');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleClose = () => {
    setSyncResult(null);
    setSyncError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <SettingsSlideModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Sync Facebook Leads"
      subtitle={`Form ID: ${form.form_id}`}
      icon={<Database className="w-6 h-6" />}
    >
      <>
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Lead Sync Info - Compact */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-900">{form.form_name}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    form.form_status === 'ACTIVE'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {form.form_status}
                  </span>
                  {form.field_mapping && Object.keys(form.field_mapping).length > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                      {Object.keys(form.field_mapping).length} fields mapped
                    </span>
                  )}
                </div>
                {form.last_sync_at && (
                  <span className="text-xs text-gray-500">
                    Last sync: {new Date(form.last_sync_at).toLocaleDateString()} {new Date(form.last_sync_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            </div>

            {/* Show sync result if available */}
            {syncResult ? (
              <div className="space-y-4">
                {/* Results Summary */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Sync Results</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <Users className="h-5 w-5 text-gray-500" />
                        <span className="text-2xl font-bold text-gray-700">{syncResult.total}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Total from Facebook</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span className="text-2xl font-bold text-green-600">{syncResult.synced}</span>
                      </div>
                      <p className="text-sm text-green-700 mt-1">New Leads Added</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <AlertCircle className="h-5 w-5 text-yellow-500" />
                        <span className="text-2xl font-bold text-yellow-600">{syncResult.duplicates}</span>
                      </div>
                      <p className="text-sm text-yellow-700 mt-1">Duplicates Skipped</p>
                    </div>
                    {syncResult.errors > 0 && (
                      <div className="bg-red-50 rounded-lg p-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <XCircle className="h-5 w-5 text-red-500" />
                          <span className="text-2xl font-bold text-red-600">{syncResult.errors}</span>
                        </div>
                        <p className="text-sm text-red-700 mt-1">Errors</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Success/Info Message */}
                {syncResult.synced > 0 ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-green-700 font-medium">
                        Successfully synced {syncResult.synced} new leads to your CRM!
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-5 w-5 text-blue-500" />
                      <span className="text-blue-700">
                        No new leads to sync. All leads from the selected date range are already in your CRM.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Date Range Filter */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <Calendar className="h-5 w-5 text-gray-600" />
                    <h3 className="font-medium text-gray-900">Select Date Range</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        From Date
                      </label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        disabled={isSyncing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent disabled:bg-gray-100"
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        To Date
                      </label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        disabled={isSyncing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent disabled:bg-gray-100"
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700">
                      <strong>Note:</strong> Only leads within the selected date range will be synced. Duplicates (by phone number or Facebook Lead ID) are automatically skipped.
                    </p>
                  </div>
                </div>

                {/* Error Message */}
                {syncError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <XCircle className="h-5 w-5 text-red-500" />
                      <span className="text-red-700">{syncError}</span>
                    </div>
                  </div>
                )}

                {/* Syncing State */}
                {isSyncing && (
                  <div className="flex items-center justify-center p-8 border border-gray-200 rounded-lg">
                    <div className="flex flex-col items-center space-y-3">
                      <RefreshCw className="h-8 w-8 animate-spin text-brandColor" />
                      <span className="text-gray-600">Syncing leads from Facebook...</span>
                      <span className="text-xs text-gray-500">This may take a moment</span>
                    </div>
                  </div>
                )}

                {/* Mapping Warning */}
                {(!form.field_mapping || Object.keys(form.field_mapping).length === 0) && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      <div>
                        <h4 className="font-medium text-red-900">No Field Mapping Configured</h4>
                        <p className="text-sm text-red-700 mt-1">
                          Please configure field mapping before syncing leads.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-white">
          <div className="flex justify-end space-x-3">
            {syncResult ? (
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover transition-colors"
              >
                Done
              </button>
            ) : (
              <>
                <button
                  onClick={handleClose}
                  disabled={isSyncing}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSync}
                  disabled={isSyncing || !form.field_mapping || Object.keys(form.field_mapping).length === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {isSyncing ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  <span>{isSyncing ? 'Syncing...' : 'Sync Leads'}</span>
                </button>
              </>
            )}
          </div>
        </div>
      </>
    </SettingsSlideModal>
  );
};

export default FacebookLeadSync;
